import { useMemo, useState } from "react";
import {
  BrainRun,
  BrainRunStatus,
  RunMetricKind,
  RUN_STATUS_COLOR,
  durationMsOf,
  formatBilling,
  formatNumber,
  tokensOf,
} from "../api/types";

const W = 1000;
const LABEL_W = 168;
const RIGHT_PAD = 72;
const ROW_H = 24;
const AXIS_H = 22;

const fmtMs = (ms: number) =>
  ms >= 1000 ? `${(ms / 1000).toFixed(2)}s` : `${Math.round(ms)}ms`;

const valueOf = (r: BrainRun, kind: RunMetricKind) => {
  if (kind === "tokens") return tokensOf(r);
  if (kind === "billing") return r.billingWeight;
  return durationMsOf(r);
};

const fmtValue = (v: number, kind: RunMetricKind) => {
  if (kind === "tokens") return formatNumber(v);
  if (kind === "billing") return formatBilling(v);
  return fmtMs(v);
};

const axisLabel = (kind: RunMetricKind) =>
  kind === "tokens"
    ? "Tokens (in + out + cache)"
    : kind === "billing"
      ? "Billing weight"
      : "Run duration";

interface Props {
  runs: BrainRun[];
}

const RunWaterfallChart: React.FC<Props> = ({ runs }) => {
  const [kind, setKind] = useState<RunMetricKind>("duration");

  const rows = useMemo(
    () =>
      [...runs]
        .sort(
          (a, b) =>
            new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()
        )
        .slice(0, 16)
        .map((r) => ({
          run: r,
          label: `#${r.id} · ${r.tenant}`,
          color: RUN_STATUS_COLOR[r.status as BrainRunStatus] ?? "#94a3b8",
          value: valueOf(r, kind),
        })),
    [runs, kind]
  );

  const max = Math.max(...rows.map((r) => r.value), 1);
  const chartH = rows.length * ROW_H + AXIS_H;
  const plotW = W - LABEL_W - RIGHT_PAD;

  if (rows.length === 0) {
    return (
      <p className="text-sm text-slate-500 font-mono py-6 text-center">
        No runs in range — trigger a sync or widen filters.
      </p>
    );
  }

  return (
    <div>
      <div className="flex justify-end gap-1 mb-3">
        {(["duration", "tokens", "billing"] as RunMetricKind[]).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setKind(k)}
            className={`px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider border rounded-sm ${
              kind === k
                ? "bg-[#C9A260] text-[#111110] border-[#C9A260]"
                : "bg-transparent text-[#9D9585] border-[#262420] hover:border-[#C9A260] hover:text-[#C9A260]"
            }`}
          >
            {k}
          </button>
        ))}
      </div>

      <svg
        viewBox={`0 0 ${W} ${chartH}`}
        className="w-full h-auto"
        role="img"
        aria-label={`Run ${kind} chart`}
      >
        {[0, 0.25, 0.5, 0.75, 1].map((t) => {
          const x = LABEL_W + t * plotW;
          const v = max * t;
          return (
            <g key={t}>
              <line
                x1={x}
                y1={0}
                x2={x}
                y2={rows.length * ROW_H}
                stroke="#262420"
                strokeWidth={1}
              />
              <text
                x={x}
                y={chartH - 4}
                textAnchor="middle"
                className="fill-[#5A5448]"
                style={{ fontSize: 9, fontFamily: "monospace" }}
              >
                {fmtValue(v, kind)}
              </text>
            </g>
          );
        })}

        {rows.map((row, i) => {
          const y = i * ROW_H + 4;
          const barW = (row.value / max) * plotW;
          return (
            <g key={row.run.id}>
              <text
                x={LABEL_W - 8}
                y={y + 12}
                textAnchor="end"
                className="fill-[#9D9585]"
                style={{ fontSize: 10, fontFamily: "monospace" }}
              >
                {row.label}
              </text>
              <rect
                x={LABEL_W}
                y={y}
                width={Math.max(barW, 2)}
                height={16}
                fill={row.color}
                rx={1}
              />
              <text
                x={LABEL_W + barW + 6}
                y={y + 12}
                className="fill-[#9D9585]"
                style={{ fontSize: 9, fontFamily: "monospace" }}
              >
                {fmtValue(row.value, kind)}
              </text>
            </g>
          );
        })}
      </svg>
      <p className="text-[10px] text-[#5A5448] font-mono mt-1">{axisLabel(kind)}</p>
    </div>
  );
};

export default RunWaterfallChart;